/**
 * API Client - Centralized HTTP client with interceptors
 * Following Meta patterns: typed, error-handled, retry logic
 * 
 * Supports BOTH Firebase token AND API key authentication:
 * - Firebase token: For authenticated dashboard/user operations
 * - API key: For TTS generation with quota tracking
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { auth } from '@/config/firebase';
import type {
  APIKey,
  PricingPlan,
  UsageStats,
  TTSRequest,
  TTSResponse,
  APIError,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

class APIClient {
  private client: AxiosInstance;
  private firebaseToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token (Firebase or API key)
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Try Firebase token first (for authenticated users)
        const firebaseToken = await this.getFirebaseToken();
        if (firebaseToken && config.headers) {
          config.headers.Authorization = `Bearer ${firebaseToken}`;
          return config;
        }
        
        // Fall back to API key
        const apiKey = this.getStoredAPIKey();
        if (apiKey && config.headers) {
          config.headers['X-API-Key'] = apiKey;
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    // Response interceptor - Handle errors globally
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<APIError>) => {
        if (error.response?.status === 401) {
          // Don't redirect for public endpoints
          const publicEndpoints = ['/tts', '/voices', '/health', '/api/v1/pricing'];
          const isPublicEndpoint = publicEndpoints.some(ep => 
            error.config?.url?.includes(ep)
          );
          
          if (!isPublicEndpoint) {
            this.clearStoredAPIKey();
            window.location.href = '/login';
          }
        }
        return Promise.reject(this.formatError(error));
      }
    );
  }

  // Get Firebase ID token for authenticated requests
  private async getFirebaseToken(): Promise<string | null> {
    try {
      const user = auth.currentUser;
      if (user) {
        // Get fresh token (auto-refreshes if needed)
        this.firebaseToken = await user.getIdToken();
        return this.firebaseToken;
      }
    } catch (error) {
      console.warn('Failed to get Firebase token:', error);
    }
    return null;
  }

  private formatError(error: AxiosError<APIError>): Error {
    const message = error.response?.data?.detail || error.message || 'An error occurred';
    const statusCode = error.response?.status;
    const formattedError = new Error(message);
    (formattedError as any).statusCode = statusCode;
    (formattedError as any).response = error.response; // Preserve full response
    return formattedError;
  }

  private getStoredAPIKey(): string | null {
    return localStorage.getItem('amebogpt_api_key');
  }

  private clearStoredAPIKey(): void {
    localStorage.removeItem('amebogpt_api_key');
  }

  setAPIKey(key: string): void {
    localStorage.setItem('amebogpt_api_key', key);
  }

  // API Methods
  async getPricing(): Promise<PricingPlan[]> {
    const response = await this.client.get<PricingPlan[]>('/api/v1/pricing');
    return Array.isArray(response.data) ? response.data : (response.data as any).plans || [];
  }

  async getAPIKeys(): Promise<APIKey[]> {
    const response = await this.client.get<APIKey[]>('/api/v1/keys');
    return response.data;
  }

  async createAPIKey(name?: string, plan: string = 'free'): Promise<{ api_key: string; message: string }> {
    const response = await this.client.post<{ api_key: string; message: string }>('/api/v1/keys', {
      name,
      plan,
    });
    return response.data;
  }

  async revokeAPIKey(keyId: number): Promise<void> {
    await this.client.delete(`/api/v1/keys/${keyId}`);
  }

  async getUsageStats(): Promise<UsageStats> {
    const response = await this.client.get<UsageStats>('/api/v1/usage');
    return response.data;
  }

  async generateTTS(request: TTSRequest): Promise<TTSResponse> {
    // TTS can take longer on first request (model cold start)
    // Using 120 second timeout for TTS requests
    const response = await this.client.post<TTSResponse>('/tts', request, {
      timeout: 120000, // 2 minutes for TTS
    });
    return response.data;
  }

  async getVoices(): Promise<any> {
    const response = await this.client.get('/voices/all');
    return response.data;
  }

  async healthCheck(): Promise<{ status: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // ============================================
  // NEW: Firebase-authenticated endpoints
  // ============================================

  /**
   * Verify Firebase token with backend and sync user
   * Called after Firebase login to create/update backend user record
   */
  async verifyAndSyncUser(): Promise<{
    status: string;
    user: {
      id: number;
      firebase_uid: string;
      email: string;
      display_name: string;
      plan: string;
      characters_used_this_month: number;
      requests_this_month: number;
    };
  }> {
    const response = await this.client.post('/api/v1/auth/verify');
    return response.data;
  }

  /**
   * Get current user profile from backend
   */
  async getCurrentUser(): Promise<{
    id: number;
    firebase_uid: string;
    email: string;
    display_name: string;
    plan: string;
    characters_used_this_month: number;
    requests_this_month: number;
    total_characters: number;
    total_requests: number;
  }> {
    const response = await this.client.get('/api/v1/auth/me');
    return response.data;
  }

  /**
   * Get API keys for authenticated user
   */
  async getUserAPIKeys(): Promise<APIKey[]> {
    const response = await this.client.get('/api/v1/auth/keys');
    return response.data;
  }

  /**
   * Create new API key for authenticated user
   */
  async createUserAPIKey(name?: string): Promise<{
    api_key: string;
    message: string;
    plan: string;
  }> {
    const response = await this.client.post('/api/v1/auth/keys', { name });
    return response.data;
  }

  /**
   * Revoke API key for authenticated user
   */
  async revokeUserAPIKey(keyId: number): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/v1/auth/keys/${keyId}`);
    return response.data;
  }

  /**
   * Get usage stats for authenticated user
   */
  async getUserUsageStats(): Promise<UsageStats> {
    const response = await this.client.get('/api/v1/auth/usage');
    return response.data;
  }

  /**
   * Check auth system status
   */
  async getAuthStatus(): Promise<{
    firebase_configured: boolean;
    database_available: boolean;
    message: string;
  }> {
    const response = await this.client.get('/api/v1/auth/status');
    return response.data;
  }

  // ===========================================
  // Vapi.ai Voice Assistant Methods
  // ===========================================

  /**
   * Check Vapi.ai service status
   */
  async getVapiStatus(): Promise<{
    configured: boolean;
    available: boolean;
    message: string;
  }> {
    const response = await this.client.get('/api/v1/vapi/status');
    return response.data;
  }

  /**
   * Create a web call configuration
   */
  async createWebCall(assistantId?: string): Promise<{
    call_id: string;
    status: string;
    assistant_id: string;
    public_key: string;
    message: string;
  }> {
    const response = await this.client.post('/api/v1/vapi/calls/web', {
      assistant_id: assistantId,
    });
    return response.data;
  }

  /**
   * Create a phone call (outbound)
   */
  async createPhoneCall(
    phoneNumber: string,
    assistantId?: string
  ): Promise<{
    call_id: string;
    status: string;
    assistant_id: string;
    message: string;
  }> {
    const response = await this.client.post('/api/v1/vapi/calls/phone', {
      phone_number: phoneNumber,
      assistant_id: assistantId,
    });
    return response.data;
  }

  /**
   * Get call status
   */
  async getCallStatus(callId: string): Promise<{
    call_id: string;
    status: string;
    transcript?: string;
    duration?: number;
    metadata?: Record<string, any>;
  }> {
    const response = await this.client.get(`/api/v1/vapi/calls/${callId}`);
    return response.data;
  }

  /**
   * End a call
   */
  async endCall(callId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/v1/vapi/calls/${callId}/end`);
    return response.data;
  }
}

export const apiClient = new APIClient();

